import { renderToStaticMarkup } from 'react-dom/server';
import { RichText } from '../components/RichText';
import { reportDetailPath } from '../router';
import { formatTimestamp } from './format';
import type { ReportDetail } from '../api';

export function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function buildReportHtml(detail: ReportDetail): string {
  const permalink = `${window.location.origin}${reportDetailPath(detail.reportId)}`;
  const bodyHtml = renderToStaticMarkup(<RichText text={detail.body} />);
  const referenceRow = detail.contentUrl
    ? `<dt>Reference:</dt><dd>${escapeHtml(detail.contentUrl)}</dd>`
    : '';
  const supplementalRow = detail.supplementalUrls?.length
    ? `<dt>Supplemental links:</dt><dd>${detail.supplementalUrls.map(escapeHtml).join(', ')}</dd>`
    : '';
  return [
    '<!DOCTYPE html>',
    '<html><head><meta charset="utf-8">',
    `<title>${escapeHtml(detail.title || 'Report')}</title>`,
    '<style>body{font-family:system-ui,sans-serif;max-width:800px;margin:2rem auto;padding:0 1rem;line-height:1.6}',
    '.meta{color:#555;margin-bottom:1.5rem}.meta dt{font-weight:600;display:inline}.meta dd{display:inline;margin:0 1rem 0 0}',
    'h1{margin-bottom:0.25rem}.permalink{margin-top:2rem;font-size:0.85rem;color:#555}</style></head><body>',
    `<h1>${escapeHtml(detail.title || 'Untitled Report')}</h1>`,
    '<dl class="meta">',
    `<dt>Published:</dt><dd>${escapeHtml(formatTimestamp(detail.publishedAt))}</dd>`,
    `<dt>Source:</dt><dd>${escapeHtml(detail.source || '—')}</dd>`,
    detail.tags?.length ? `<dt>Tags:</dt><dd>${escapeHtml(detail.tags.join(', '))}</dd>` : '',
    referenceRow,
    supplementalRow,
    '</dl>',
    '<hr>',
    `<div>${bodyHtml}</div>`,
    `<p class="permalink"><a href="${escapeHtml(permalink)}">View online (with attachments)</a></p>`,
    '</body></html>',
  ].join('\n');
}

export function reportFileName(detail: ReportDetail): string {
  const base = (detail.title || 'report')
    .replace(/[^a-zA-Z0-9-_ ]/g, '')
    .trim()
    .replace(/\s+/g, '-');
  return `${base || 'report'}.html`;
}

export function downloadReport(detail: ReportDetail): void {
  const blob = new Blob([buildReportHtml(detail)], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = reportFileName(detail);
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
